/*
    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React, { Component } from 'react';
import Tone from 'tone';
import ReactBootstrapSlider from 'react-bootstrap-slider';
import {AbstractParameter,
        ValueParameter} from 'parameters/AbstractParameter.js';
import {effectFactory} from './EffectFactory.js';
import {AbstractAudioParamParameter,
        AudioParamValueParameter,
        AudioParamPercentageValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {setEffectParameterValues} from 'actions/index.js';
import LockableEffect from './LockableEffect.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import ipfs from 'decentralized/Ipfs.js';
import {AUDIO_FX} from 'constants/effect-types.js';
import {uuid} from 'uuidv4';

var idCounter = 0;


class Effect extends LockableEffect(class {}) {
    constructor(name, effectFn, sequencerEngine) {
        super();
        this.sequencerEngine = sequencerEngine;
        this.initialBulkValues = {};
        this.store = sequencerEngine.store;
        this.name = name;
        this.id = uuid();
        this.effectFn = effectFn;
        this.enabledParameter = new AbstractParameter(
            'Enabled', this,
            (o, x) => {this.toggle(x);},
            {
                min: 0,
                max: 1,
                unit: 'boolean',
                defaultValue: true
            });
        console.log('useEffect(%s)', this.name);
        this.effectNode = effectFn(); //effectFactory.useEffect(this.name, effectFn, sequencerEngine);
        this.numberOfOutputs = 1;
        this.enabled = false;
        this.toggledValue = 0;
        this.value = 0;
        this.connected = 0;
        this.wetParameter = this.effectNode.wet !== undefined ? new AudioParamPercentageValueParameter(
            'Wet', this.effectNode.wet, {
                documentation: "Controls the amount of (wet) effected signal, vs. dry (un-effected) signal.",
                defaultValue: 0,
            }) : undefined;
       this.parameters = [];
        this.registerParameter(this.wetParameter);
        this.track = undefined;
        this.group = undefined;

        this.setWet(0);
    }

    getEffectType() {
        return AUDIO_FX;
    }

    updateInitialValues() {
        this.sequencerEngine.dispatch(
            setEffectParameterValues(this.initialBulkValues));
    }

    makePublicIfNeeded() {}

    getParameters() {
        return this.parameters || [];
    }

    isMidiEffect() {
        return false;
    }

    set wet(t) {
        if (this.wetParameter) {
            this.wetParameter.set(t);
        }
    }

    /**
     * Registers the context of this effect, i.e. which track/group/send
     * it comes from
     */
    registerContext(context, parentDevice) {
        if (this.context) {
            this.context.combineWith(context);
        } else {
            this.context = context.getCopy();
        }
        if (parentDevice) {
            this.context.forSubDevice(this);
            this.context.forEffect(parentDevice);
        } else {
            this.context.forEffect(this);
        }
        this.parameters.forEach(
            parameter =>
                parameter.setContext(this.context));
    }

    registerMixSession(mixSession) {
        this.parameters.forEach(
            parameter =>
                parameter.setMixSession(mixSession));
    }

    registerParameter(parameter) {
        if (parameter !== undefined) {
            parameter.name = parameter.name;
            parameter.effectName = this.name;
            this.initialBulkValues = {
                ... this.initialBulkValues,
                ... parameter.setStore(this.store)
            };
            this.parameters.push(parameter);
        }
    }

    finishRegistering() {
    }

    connect(node) {
        this.effectNode.connect(node);
    }

    syncable() {
        return false;
    }
    
    disconnect(node) {
        this.effectNode.disconnect(node);
    }

    isEnabled() {
        return this.enabledParameter.get();
    }
    
    enable(enabled=true) { 
        this.enabledParameter.set(enabled);
    }

    toggleEnabled() {
        this.enabledParameter.set(!this.enabledParameter.get());
    }

    toggle(enabled) {
    }

    setValue(value) {
        this.value = value;
        if (this.effectNode.wet) {
            this.wetParameter.set(value);
        }
    }

     // wet from 0 to 1
    setWet(wet) {
        if (this.effectNode.wet) {
            this.wetParameter.set(wet);
        }
    }

    getWet() {
        return this.wetParameter.value;
    }

    getName() {
        return this.name;
    }

    getEffect() {
        return this.effectNode;
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var effect = new Effect(this.name, this.effectFn, sequencerEngine);
        if (this.wetParameter) {
            effect.setWet(this.wetParameter.get());
        }
        effect.enable(this.enabledParameter.get());
        return effect;
    }

    fromJson(json, ignoreId=false) {
        if (json.id !== undefined && (!ignoreId || typeof ignoreId !== "boolean")) {
            this.id = json.id;
        }

        this.x = json.x;
        this.y = json.y;

        if (json.enabled !== undefined) {
            this.enable(json.enabled);
        }
        
        let bulk = {};
        if (this.wetParameter !== undefined) {
            if (json.wet !== undefined) {
                bulkUpdate(this.wetParameter, json.wet, bulk);
                if (this.effectNode.wet) {
                    this.effectNode.wet.value = json.wet;
                }
            }
            
            bulk = {
                ... bulk,
                ...this.loadParameterFromJson(json, 'wetParameter', this.wetParameter, ignoreId)
            };
        }

        return bulk;
    }

    saveParameterToJson(json, name, parameter, isPreset=false) {
        if (parameter.isConnectedToAnyModulator() ||
            Object.keys(parameter.patternToValue).length > 0 ||
            Object.keys(parameter.stepToValue).length > 0 ||
            Object.keys(parameter.paletteToValue).length > 0 ||
            parameter.mixAutomation.automations.length > 0 || 
            parameter.automations.length >= 0 ||
            parameter.isMapped ||
            parameter.depthParameter  || 
            parameter.hasMacro) {
            let jsonCopy = {... json};
            jsonCopy[name] = parameter.getJson(isPreset);
            return jsonCopy;
        }

        return json;
    }

    loadParameterFromJson(json, fieldName, parameter, ignoreId=false) {
        ignoreId = typeof ignoreId === "boolean" && ignoreId;
        if (fieldName in json) {
            this.handleModulators(json[fieldName], parameter);
            return parameter.fromJson(json[fieldName], this.sequencerEngine, ignoreId);
        } else {
            parameter.disconnectFromAllModulators();
        }
        return {};
    }

    handleModulators(json, parameter) {
        // first check if we are connected to any LFOs that ARE NOT in the json...
        let lfosToKill = parameter.lfos.filter(
            x => !json.lfos.includes(x.number - 1));
        
        lfosToKill.forEach(lfo => parameter.disconnectFromLFO(lfo));
        
        let envelopesToKill = parameter.envelopes.filter(
            x => !json.envelopes.includes(x.number - 1));
        
        envelopesToKill.forEach(env => parameter.disconnectFromEnvelope(env));
        parameter.disableKeyScaling();
    }


    getJson() {
        let json = {
            name: this.name, 
            enabled: this.enabledParameter.get(),
            id: this.id,
            x: this.x,
            y: this.y
        };

        if (this.wetParameter !== undefined) {
            json.wet  = this.wetParameter.get(true);
            json.value  = this.wetParameter.get(true);
            json = this.saveParameterToJson(json, 'wetParameter', this.wetParameter);
        }
        return json;
    }

    dispose(useFactory=true) {
        if (this.sequencerEngine.isOffline || !useFactory) {
            if (this.effectNode) {
                this.effectNode.dispose();
            }
            this.parameters.forEach(s => s.dispose());
        } else {
            this.parameters.forEach(s => s.dispose(false));
            effectFactory.releaseEffect(this.name, this.effectNode, this.sequencerEngine);
        }
        this.effectNode = undefined;
        this.parameters = undefined;
        this.context = undefined;
        this.name = undefined;
    }
}

class FilterEffect extends Effect {
    constructor(sequencerEngine, automationRate) {
        super("Filter", () => new Tone.Filter(80, 'lowpass', -24), sequencerEngine);
        let config = {
            min: 0,
            max: 10000,
            unit: 'HZ',
            defaultValue: 80,
            documentation: [
                "Controls the frequency at which filtering works at. ",
                "Ex: For Lowpass filters, frequencies above this value will be cut."
                ]
        };

        if (automationRate) {
            config.automationRate = automationRate;
        }
        this.cutoffParameter = new AudioParamValueParameter(
            'Cutoff', this.effectNode.frequency, config
            );

        this.qParameter = new AudioParamValueParameter(
            'Q', this.effectNode.Q, {
                min: 0,
                max: 20,
                unit: '',
                defaultValue: 1
            });

        this.typeParameter = new AbstractParameter(
            'type', this, (o, type) => this.effectNode.type = type, {
                min: 'lowpass',
                max: 'highpass',
                unit: '',
                defaultValue: 'lowpass',
            });


        this.registerParameter(this.cutoffParameter);
        this.registerParameter(this.typeParameter);
        this.registerParameter(this.qParameter);

        this.updateInitialValues();
    }

    dispose() {
        super.dispose(false);
    }

    connect(node) {
        super.connect(node);
    }

    syncable() {
        return false;
    }
    
    toggle(enabled) {
    }

    setValue(value) {
        this.value = value;
        value = value * 5000 + 50;
        this.effectNode.frequency.value = value;
    }

    setFrequency(frequency) {
        try {
            this.cutoffParameter.set(frequency);
        } catch (err) {
        }
    }

    get cutoffFrequency() {
        return this.cutoffParameter.get();
    }

    set cutoffFrequency(frequency) {
        this.cutoffParameter.set(frequency);
    }

    set Q(q) {
        this.qParameter.set(q);
    }

    setQ(q) {
        this.qParameter.set(q);
    }

    lowpass() {
        this.type = 'lowpass';
    }

    hipass() {
        this.type  = 'highpass';
    }

    set type(type) {
        this.typeParameter.set(type);
    }

    get type() {
        return this.typeParameter.get();
    }

    getFrequency() {
        return this.effectNode.frequency.value;
    }

    getQ() {
        return this.effectNode.Q.value;
    }

    getJson() {
        let json = {
            ... super.getJson(),
            name: this.name,
            enabled: this.enabledParameter.get(),
            frequency: this.effectNode.frequency.value,
            q : this.effectNode.Q.value,
            type: this.effectNode.type
        };

        json = this.saveParameterToJson(json, 'cutoffParameter', this.cutoffParameter);
        json = this.saveParameterToJson(json, 'qParameter', this.qParameter);
        json = this.saveParameterToJson(json, 'typeParameter', this.typeParameter);

        return json;
    }

    fromJson(json, ignoreId) {
        let bulk = super.fromJson(json, ignoreId);
        this.enable(json.enabled);
        bulkUpdate(this.qParameter, json.q, bulk);
        bulkUpdate(this.cutoffParameter, json.frequency, bulk);
        bulkUpdate(this.typeParameter, json.type, bulk);

        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'qParameter', this.qParameter, ignoreId),
            ... this.loadParameterFromJson(json, 'cutoffParameter', this.cutoffParameter, ignoreId),
            ... this.loadParameterFromJson(json, 'typeParameter', this.typeParameter, ignoreId)
        };
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new FilterEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

class AutoFilterEffect extends Effect {
    constructor(sequencerEngine) {
        super("Auto Filter", () => new Tone.AutoFilter(4 * new Tone.Time('1m')), sequencerEngine);
        this.qParameter = new AudioParamValueParameter(
            'Q', this.effectNode.filter.Q, {
                min: 0,
                max: 20,
                unit: '',
                defaultValue: 1
            });
        this.registerParameter(this.qParameter);
        this.effectNode.start();
        this.effectNode.wet.value = 1;
        this.storedLFOTime = 4*new Tone.Time('1m');
        this.updateInitialValues();
    }

    syncable() {
        return true;
    }

    start() {
        this.effectNode.start();
    }

    stop() {
    
    this.effectNode.stop();
    }

    toggle(enabled) {
    }

    getLFOTime() {
        let time = new Tone.Time(this.effectNode.frequency.value);
        return time.toNotation();
    }

    getStoredLFOTime() {
        return this.storedLFOTime;
    }

    setLFOTime(lfoTime) {
        this.storedLFOTime = lfoTime;
        this.effectNode.frequency.value = lfoTime;
    }

    lowpass() {
        this.effectNode.filter.type = 'lowpass';
    }

    hipass() {
        this.effectNode.filter.type = 'highpass';
    }

    getFilterType() {
        return this.effectNode.filter.type;
    }

    getJson() {
        return {
            ... super.getJson(),
            name: this.name,
            enabled: this.enabledParameter.get(),
            frequency: this.effectNode.frequency.value,
            storedLFOTime: this.storedLFOTime,
            baseFrequency: this.effectNode.baseFrequency,
            octaves: this.effectNode.octaves,
            q : this.effectNode.filter.Q.value,
            filterType: this.effectNode.filter.type
        };
    }

    fromJson(json, ignoreId) {
        super.fromJson(json, ignoreId);
        this.enable(json.enabled);
        this.effectNode.filter.Q.value = json.q;
        this.effectNode.frequency.value = json.frequency;
        this.effectNode.baseFrequency = json.baseFrequency;
        this.effectNode.octaves = json.octaves;
        this.storedLFOTime = 'storedLFOTime' in json ? json.storedLFOTime : json.frequency;
        this.effectNode.filter.type = json.filterType;
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new AutoFilterEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

class PanEffect extends Effect {
    constructor(sequencerEngine) {
        super("Pan", () => new Tone.Panner(), sequencerEngine);
        this.panParameter = new AbstractParameter(
            'Panning', this.effectNode.pan, (o, x) => o.value = x,
            {
                min: -1,
                max: 1,
                unit: '',
                defaultValue: 0
            });
        this.registerParameter(this.panParameter);
        this.updateInitialValues();
    }

    toggle(enabled) {
    }
    
    setValue(value) {
        this.value = value;
        this.panParameter.normalizedSet(value);
    }

    getJson() {
        return {
            ... super.getJson(),
            name: this.name, 
            enabled: this.enabledParameter.get(),
            value: this.value};
    }

    fromJson(json, ignoreId) {
        super.fromJson(json, ignoreId);
        let pan = new PanEffect();
        pan.setValue(this.value);
        return pan;
    }

    getEffectCopy(sequencerEngine=this.sequencerEnginei, ignoreId=true) {
        var copy = new PanEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

class ReverbEffect extends Effect {
    constructor(sequencerEngine) {
        super("Reverb", () => new Tone.Freeverb(), sequencerEngine);
        this.roomSizeParameter = new AudioParamPercentageValueParameter(
            'Room Size', this.effectNode.roomSize);
        this.registerParameter(this.roomSizeParameter);
        this.dampeningParameter = new AbstractAudioParamParameter(
            'Dampening', this.effectNode.dampening, (o, d) => o.value = d,
            {
                min: 0,
                max: 10000
            });
        this.registerParameter(this.dampeningParameter);
        this.updateInitialValues();
    }

    setRoomSize(roomSize) {
        this.roomSizeParameter.set(roomSize);
    }

    setDampening(dampening) {
        this.dampening.set(dampening);
    }

    getDampening() {
        return this.effectNode.dampening.value;
    }

    getRoomSize() {
        return this.effectNode.roomSize.value;
    }

    getEffectCopy(sequencerEngine=this.sequencerEnginei, ignoreId=true) {
        let copy = new ReverbEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    getJson() {
        let json = {
            ... super.getJson(),
            name: this.name, 
            enabled: this.enabledParameter.get(),
            roomSize: this.roomSizeParameter.get(),
            wet: this.wetParameter.get(),
            dampening: this.dampeningParameter.get()};

        if (this.roomSizeParameter.isConnectedToAnyModulator() ||
            Object.keys(this.roomSizeParameter.patternToValue).length > 0) {
            json.roomSizeParameter = this.roomSizeParameter.getJson();
        }

        if (this.dampeningParameter.isConnectedToAnyModulator() ||
            Object.keys(this.dampeningParameter.patternToValue).length > 0) {
            json.dampeningParameter = this.dampeningParameter.getJson();
        }

        return json;
    }

    fromJson(json, ignoreId) {
        super.fromJson(json, ignoreId);
        this.name = json.name;
        this.enable(json.enabled);
        if ('roomSize' in json) {
            this.roomSizeParameter.set(json.roomSize);
        }
        if ('wet' in json) {
            this.wetParameter.set(json.wet);
        }
        if ('dampening' in json) {
            this.dampeningParameter.set(json.dampening);
        }

        this.loadParameterFromJson(json, 'roomSizeParameter', this.roomSizeParameter, ignoreId);
        this.loadParameterFromJson(json, 'dampeningParameter', this.dampeningParameter, ignoreId);
    }
}

class TremoloEffect extends Effect {
    constructor(sequencerEngine) {
        super("Tremolo", () => new Tone.Tremolo(), sequencerEngine);
        this.effectNode.start();
        this.rateParameter = new AbstractAudioParamParameter(
            'Rate', this.effectNode.frequency, (o, x) => o.value = x,
            {
                min: 0,
                max: 32,
                defaultValue: 0
            });
        this.spreadParameter = new AbstractAudioParamParameter(
            'Spread', this.effectNode, (o, x) => o.spread = x,
            {
                min: 0,
                max: 360,
                defaultValue: 180
            });
        this.depthParameter = new AudioParamPercentageValueParameter(
            'Depth', this.effectNode.depth);
        this.registerParameter(this.depthParameter);
        this.registerParameter(this.rateParameter);
        this.registerParameter(this.spreadParameter);
        this.updateInitialValues();
    }

    setRate(frequency) {
        this.effectNode.frequency.value = frequency;
    }

    setDepth(depth) {
        this.effectNode.depth.value = depth;
    }

    getRate() {
        return this.effectNode.frequency.value;
    }

    getDepth() {
        return this.effectNode.depth.value;
    }

    getEffectCopy(sequencerEngine=this.sequencerEnginei, ignoreId=true) {
        let copy = new TremoloEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    getJson() {
        let json = {
            ... super.getJson(),
            name: this.name, 
            enabled: this.enabledParameter.get(),
            depth: this.depthParameter.get(),
            spread: this.spreadParameter.get(),
            rate: this.rateParameter.get()};

        if (this.depthParameter.isConnectedToAnyModulator() ||
            Object.keys(this.depthParameter.patternToValue).length > 0) {
            json.depthParameter = this.depthParameter.getJson();
        }

        if (this.spreadParameter.isConnectedToAnyModulator() ||
            Object.keys(this.spreadParameter.patternToValue).length > 0) {
            json.spreadParameter = this.spreadParameter.getJson();
        }

        if (this.rateParameter.isConnectedToAnyModulator() ||
            Object.keys(this.rateParameter.patternToValue).length > 0) {
            json.rateParameter = this.rateParameter.getJson();
        }

        return json;
    }

    fromJson(json, ignoreId) {
        super.fromJson(json, ignoreId);
        this.name = json.name;
        this.enable(json.enabled);
        this.depthParameter.set(json.depth !== undefined ? json.depth : 1);
        this.spreadParameter.set(json.spread !== undefined ? json.spread : 180);
        this.wetParameter.set(json.wet !== undefined ? json.wet : 1);
        this.rateParameter.set(json.rate !== undefined ? json.rate : 1);

        this.loadParameterFromJson(json, 'depthParameter', this.depthParameter, ignoreId);
        this.loadParameterFromJson(json, 'spreadParameter', this.spreadParameter, ignoreId);
        this.loadParameterFromJson(json, 'rateParameter', this.rateParameter, ignoreId);
    }
}

class ChorusEffect extends Effect {
    constructor(sequencerEngine) {
        super("Chorus", () => new Tone.Chorus(), sequencerEngine);
        this.rateParameter = new AbstractAudioParamParameter(
            'Rate', this.effectNode.frequency, (o, x) => o.value = x,
            {
                min: 0,
                max: 32,
                defaultValue: 0.8
            });
        this.registerParameter(this.rateParameter);
        this.depthParameter = new AbstractAudioParamParameter(
            'Depth', this.effectNode, (o, x) => o.depth = x, {
                min: 0,
                max: 100,
                unit: '%',
                automatable: false,
                defaultValue: 0.5
            });
        this.registerParameter(this.depthParameter);
        this.updateInitialValues();
    }

    setRate(frequency) {
        this.effectNode.frequency.value = frequency;
    }

    setDepth(depth) {
        this.effectNode.depth = depth;
    }

    getRate() {
        return this.effectNode.frequency.value;
    }

    getDepth() {
        return this.effectNode.depth;
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        let copy = new ChorusEffect(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }

    dispose() {
        super.dispose(true);
    }

    getJson() {
        let json = {
            ... super.getJson(),
            name: this.name, 
            enabled: this.enabledParameter.get(true),
            depth: this.depthParameter.get(true),
            rate: this.rateParameter.get(true)};

        json = this.saveParameterToJson(json, 'depthParameter', this.depthParameter);
        json = this.saveParameterToJson(json, 'rateParameter', this.rateParameter);

        return json;
    }

    fromJson(json, ignoreId) {
        let bulk = super.fromJson(json, ignoreId);
        this.name = json.name;
        this.enable(json.enabled);
        bulkUpdate(this.wetParameter, json.wet, bulk);
        bulkUpdate(this.depthParameter, json.depth, bulk);
        bulkUpdate(this.rateParameter, json.rate, bulk);

        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'rateParameter', this.rateParameter, ignoreId),
            ... this.loadParameterFromJson(json, 'depthParameter', this.depthParameter, ignoreId)
        };
    }
}


const Effects = {
    ReverbEffect: ReverbEffect,
    FilterEffect: FilterEffect,
    TremoloEffect: TremoloEffect,
    AutoFilterEffect: AutoFilterEffect,
    ChorusEffect: ChorusEffect,
    PanEffect: PanEffect,
    Effect:Effect};
export default Effects;
